/**
 * The optional columns of a review screen, in the order every screen shows
 * them. The gutter is not among them: it goes with the document.
 */
export type ReviewColumn = "document" | "secondDocument" | "cards" | "rail";

export const REVIEW_COLUMN_ORDER: readonly ReviewColumn[] = [
	"document",
	"secondDocument",
	"cards",
	"rail",
];

const SEPARATOR = ",";

export function canToggle(
	open: ReadonlySet<ReviewColumn>,
	column: ReviewColumn,
): boolean {
	if (!open.has(column)) return true;
	return leavesSomethingToRead(withoutColumn(open, column));
}

export function armsShown(open: ReadonlySet<ReviewColumn>): boolean {
	return open.has("document") && open.has("cards");
}

export function serialiseOpenColumns(open: ReadonlySet<ReviewColumn>): string {
	return REVIEW_COLUMN_ORDER.filter((column) => open.has(column)).join(
		SEPARATOR,
	);
}

/** Null when nothing usable was stored, so the screen keeps its defaults. */
export function parseOpenColumns(
	stored: string | null,
	declared: readonly ReviewColumn[],
): ReadonlySet<ReviewColumn> | null {
	if (stored === null) return null;
	const named = new Set(stored.split(SEPARATOR));
	const open = new Set(declared.filter((column) => named.has(column)));
	return leavesSomethingToRead(open) ? open : null;
}

function leavesSomethingToRead(open: ReadonlySet<ReviewColumn>): boolean {
	return open.has("document") || open.has("cards");
}

function withoutColumn(
	open: ReadonlySet<ReviewColumn>,
	column: ReviewColumn,
): ReadonlySet<ReviewColumn> {
	return new Set([...open].filter((other) => other !== column));
}
